import type { ComponentProps } from 'react';
import {
  Flex,
  SafeAreaTopSpacing,
  StackScreen,
  useScreenSize,
} from '@banhala/ui-kit';
import { LoadingWebView } from '../LoadingWebView';
import { isNotNil } from '../../utils';
import { useWebViewActionFromMessage } from '../../hooks';

type WebviewScreenLayoutProps = {
  /** 네이티브 헤더 타이틀. `showHeader` 가 false 면 화면 title(문서 타이틀)로만 쓰인다. */
  title?: string;
  /**
   * 네이티브(StackScreen) 헤더를 그릴지.
   *
   * 기본 false — 대부분의 웹뷰 페이지가 자체 타이틀바를 그리기 때문이다(`title_bar=false`
   * 계약). 그 경우 네이티브 헤더를 숨기고 상단 세이프에어리어만 확보한다.
   */
  showHeader?: boolean;
  hideBackButton?: boolean;
  /** StackScreen 의 아이콘 타입을 그대로 파생시켜 쓴다(직접 선언하면 좁은 타입과 어긋난다). */
  rightIcons?: ComponentProps<typeof StackScreen>['rightIcons'];
  onPressBack?: () => void;
  /** `useWebviewUri` 로 만든 URL. 아직 준비되지 않았으면 undefined(웹뷰를 렌더하지 않음). */
  uri?: string;
  /** 콘텐츠가 postMessage 로 타이틀을 알려줄 때. (프로토콜: `{ type: 'setTitle' }`) */
  onChangeTitle?: (title: string) => void;
};

/**
 * 웹뷰 한 장을 화면 전체에 띄우는 공통 셸.
 *
 * Cart / OrderDetail / EventDetail / TabWebView 등 "전용 화면인데 내용은 웹뷰" 인 화면들이
 * StackScreen + 세이프에어리어 + 가운데 정렬 컨테이너 + WebView + 메시지 브리지를 각자
 * 똑같이 조립하고 있어 한 곳으로 모았다.
 *
 * 가로 폭을 `useScreenSize().width` 로 고정하고 가운데 정렬하는 이유: 데스크톱 웹에서
 * 콘텐츠가 화면 전체로 늘어나지 않게 모바일 폭을 유지한다(ADSContext.contentWidth 반영).
 */
export const WebviewScreenLayout = ({
  title,
  showHeader = false,
  hideBackButton,
  rightIcons,
  onPressBack,
  uri,
  onChangeTitle,
}: WebviewScreenLayoutProps) => {
  const { width: screenWidth } = useScreenSize();
  const webViewAction = useWebViewActionFromMessage({ onChangeTitle });

  return (
    <StackScreen
      title={title}
      hideHeader={!showHeader}
      hideBackButton={hideBackButton}
      rightIcons={rightIcons}
      onPressBack={onPressBack}
    >
      {/* 네이티브 헤더가 없을 때만(=웹뷰가 타이틀바를 그림) 상단 세이프에어리어를 직접 확보 */}
      {!showHeader && <SafeAreaTopSpacing />}
      <Flex flex={1} alignItems={'center'}>
        <Flex width={screenWidth} flex={1}>
          {/* key: URL 이 바뀌면 로딩 상태를 처음부터 다시 시작한다 */}
          {isNotNil(uri) && (
            <LoadingWebView key={uri} uri={uri} onMessage={webViewAction} />
          )}
        </Flex>
      </Flex>
    </StackScreen>
  );
};
